// i18n (Phase 14 performance budget, spec §15): every locale × namespace JSON is its own lazy chunk, loaded through a
// tiny i18next backend. Only the default locale's `common` + `shell` ship in the entry; the shell chunk, each route
// and lazy features load the namespaces they render (`ensureNamespaces`, `withNamespaces`) in parallel with their
// code, so nothing waits on a second round trip and nobody downloads the other language. See DECISIONS › Phase 14.
import { useEffect, useMemo, useReducer } from 'react';
import i18n, { type BackendModule, type TFunction } from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import trCommon from './locales/tr/common.json';
import trShell from './locales/tr/shell.json';

export const LOCALES = ['tr', 'en'] as const;
export type AppLocale = (typeof LOCALES)[number];

export const NAMESPACES = [
  'common',
  'auth',
  'onboarding',
  'settings',
  'shell',
  'community',
  'communitySettings',
  'chat',
  'threads',
  'search',
  'moderation',
  'safety',
  'home',
  'notifications',
  'events',
  'discover',
  'social',
  'profile',
  'voice',
  'mobile',
  'desktop',
  'boards',
  'staff',
  'recordings',
  /** MOD ekonomisi: Market, wallet, "MOD nasıl kazanılır", the earning toast */
  'market',
  /** Sinema Salonu: the salon page, "Sinema salonu aç", the channel strip */
  'cinema',
  /** MOODS Arena: Takım Bul, open listings, team rooms */
  'arena',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

/**
 * Namespaces rendered by the signed-in shell chrome and the hosts it mounts (rail, sidebars, user dock, topbar
 * controls, banners). Loaded together with the shell chunk; `test/i18n-namespaces.test.ts` checks this list against
 * the shell's import graph.
 */
export const SHELL_NAMESPACES: Namespace[] = ['common', 'shell', 'mobile', 'notifications'];

/**
 * Copy the shell chrome shows only after an interaction or an event — context menus and their dialogs, error and
 * realtime toasts, hover previews — read with `useLateTranslation`. AppShell loads it once the first screen settled,
 * the UI kit's overlays wait for it, and screens that render it list it like any other namespace (Phase 14 budget).
 */
export const SHELL_LATE_NAMESPACES: Namespace[] = ['community', 'social'];

// one chunk per file (`./locales/en/chat.json` …); the two bundled Turkish files are never fetched
const loaders = import.meta.glob<{ default: Record<string, unknown> }>(['./locales/*/*.json', '!./locales/tr/common.json', '!./locales/tr/shell.json']);

const backend: BackendModule = {
  type: 'backend',
  init() {},
  read(lng, ns, cb) {
    const load = loaders[`./locales/${lng}/${ns}.json`];
    if (!load) {
      cb(new Error(`i18n: no ${lng}/${ns}`), false);
      return;
    }
    load().then(
      m => cb(null, m.default),
      (err: unknown) => cb(err instanceof Error ? err : new Error(String(err)), false),
    );
  },
};

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

const stored = typeof localStorage !== 'undefined' ? safeGet('moods.locale') : null;
// Turkish is the default UI language (spec §1); English only when chosen (stored) or set on the account.
const initial: AppLocale = stored === 'en' || stored === 'tr' ? stored : 'tr';

/** Resolves once i18next is initialised with `common` + `shell` of the current locale (main.tsx renders after it). */
export const i18nReady: Promise<unknown> = i18n
  .use(backend)
  .use(initReactI18next)
  .init({
    lng: initial,
    supportedLngs: [...LOCALES],
    // `pnpm i18n:check` keeps tr and en complete, so a fallback would only download the other language's files
    fallbackLng: false,
    defaultNS: 'common',
    ns: ['common', 'shell'],
    partialBundledLanguages: true,
    resources: { tr: { common: trCommon, shell: trShell } },
    interpolation: { escapeValue: false },
    returnNull: false,
  });

/** Loads namespaces of the current locale (no-op for loaded ones); later language switches load them too. */
export function ensureNamespaces(ns: readonly Namespace[]): Promise<void> {
  const missing = ns.filter(n => !i18n.hasLoadedNamespace(n));
  if (!missing.length) return Promise.resolve();
  return i18n.loadNamespaces(missing).then(() => undefined);
}

const loadedNs = (ns: Namespace) => i18n.hasLoadedNamespace(ns);

/**
 * `useTranslation` for `SHELL_LATE_NAMESPACES` in components of the shell chrome: never suspends and never starts a
 * load. `t` reads the store when it is called (menus build their entries on open, toasts and error copy run later)
 * and the component renders again once the namespace arrived (or the language changed).
 */
export function useLateTranslation(ns: Namespace): { t: TFunction; i18n: typeof i18n } {
  // re-renders on language changes (common is always there)
  const { i18n: instance } = useTranslation();
  const [, arrived] = useReducer((n: number) => n + 1, 0);
  const ready = loadedNs(ns);
  useEffect(() => {
    if (ready) return;
    const onLoaded = () => {
      if (loadedNs(ns)) arrived();
    };
    instance.on('loaded', onLoaded);
    // arrived between the render and this effect
    onLoaded();
    return () => instance.off('loaded', onLoaded);
  }, [instance, ns, ready]);
  const lng = instance.language;
  const t = useMemo(() => instance.getFixedT(lng, ns), [instance, lng, ns]);
  return { t, i18n: instance };
}

/** Whether a namespace of the current locale is there (fallback copy for the moment before a late one arrives). */
export const hasNamespace = loadedNs;

/** A lazy-import factory that also loads the namespaces its screen renders, in parallel with the code. */
export function withNamespaces<T>(load: () => Promise<T>, ns: readonly Namespace[]): () => Promise<T> {
  return () => Promise.all([load(), ensureNamespaces(ns)]).then(([m]) => m);
}

export function setLocale(locale: AppLocale): void {
  void i18n.changeLanguage(locale);
  document.documentElement.lang = locale;
  try {
    localStorage.setItem('moods.locale', locale);
  } catch {
    // storage unavailable
  }
}

export default i18n;
